import type { FC } from 'react';
import { useLayoutEffect, useMemo } from 'react';
import useFarm from '../../data/hooks/collections/useFarm';
import FarmContext from './FarmContext';
import type { ColumnSpec, MCPConfig } from '@phyllome/common';
import { MCPConfigColumnSpec } from '@phyllome/common';
import LoadingDialog from '../LoadingDialog/LoadingDialog';
import useRoute from '../../data/hooks/useRoute';
import { useAtom } from 'jotai';
import { brokerIdAtom } from '../../data/atoms/brokerIdAtom';

function getBrokerFromPath(str: string) {
  const regex = /\/broker\/([^/]+)/;

  const match = str.match(regex);

  return match && match[1]; // Extract the captured data
}
export type FarmState = MCPConfig & {
  columnSpec: ColumnSpec[];
};

const FarmProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const [storedBrokerId, setBrokerId] = useAtom(brokerIdAtom);

  const route = useRoute();

  const brokerId = getBrokerFromPath(route.mainPath);

  const farmData = useFarm(brokerId || '');

  // Derived during render, so it always belongs to the farm in the URL. When it
  // was state set by an effect, a farm switch rendered one pass with the old farm.
  const farmState = useMemo<FarmState | undefined>(() => {
    if (!farmData) return undefined;

    return {
      ...farmData,
      columnSpec: MCPConfigColumnSpec(farmData),
    };
  }, [farmData]);

  // A layout effect, so the re-render that the check below waits for happens
  // before the browser paints.
  useLayoutEffect(() => {
    setBrokerId(brokerId || undefined);
  }, [brokerId, setBrokerId]);

  if (!farmState && brokerId) {
    // Then wait a little longer for the farm data to arrive.
    return (
      <LoadingDialog>
        loading <strong>{brokerId}</strong>
      </LoadingDialog>
    );

  }

  // Pages read brokerIdAtom (useBrokerId, getFetch). Hold them back until the
  // layout effect above has caught it up with the URL, so that they never render
  // with '' or the previous farm's id.
  if ((brokerId || undefined) !== storedBrokerId) return null;

  if (!farmState) return <>{children}</>;
  return (
    <FarmContext.Provider value={farmState}>{children}</FarmContext.Provider>
  );
};

export default FarmProvider;
